import { List, Button, CloseButton, Drawer, DrawerContent, Heading, Select, DrawerHeader, DrawerBody, HStack } from '@chakra-ui/react'
import { IComment, ICommentSet } from '@/shared/IComment'
import { Model } from "survey-core";
import { AuthService } from '@/app/services/AuthService';
import { CommentsService } from '@/app/services/CommentsService';
import { makeGuidString } from '@/shared/guid';
import { truncate } from '@/shared/parse';
import React from 'react';
import { CommentTread } from './CommentThread';

export const CommentSidebar = (props: {
  survey: Model;

  allowPin: boolean;
  size?: 'xs' | 'sm';

  isOpen: boolean;
  onClose: () => void;

  courseId: string;
  moduleId: string;
  pageId?: string;
  questionId?: string;

  triggerCount?: number;

  allCommentSet: ICommentSet;
  setAllCommentSet: (comments: ICommentSet) => void;
}) => {

  const allCommentSet = React.useMemo(() => {
    const result: ICommentSet = {};
    for (const key in props.allCommentSet) {
      const comment = {...props.allCommentSet[key]};

      // fix page ids
      if (!comment.page && comment.questionId) {
        const question = CommentsService.getQuestionFromPath(props.survey, comment.questionId);
        if (question && question.page) {
          comment.page = question.page.name;
        }
      }

      result[key] = comment;
    }
    return result;
  }, [props.allCommentSet, props.survey]);


  const [editCommentId, setEditCommentId] = React.useState<string>();

  const [filterQuestionId, setFilterQuestionId] = React.useState(props.questionId);

  // hack to update the button title
  const onSendComment = async (comment: IComment) => {
    const updated = await CommentsService.putCourseComment(comment)
    props.setAllCommentSet(updated);
    setNewComment(undefined);
    setEditCommentId(undefined);
  }

  const onEditComment = async (comment: IComment) => {
    setEditCommentId(comment.id);
  }

  const onDeleteComment = async (comment: IComment) => {
    const updated = await CommentsService.deleteCourseComment(comment);
    props.setAllCommentSet(updated);
  }

  const onCancelEdit = () => {
    setNewComment(undefined);
    setEditCommentId(undefined);
  }

  const onNavigate = (comment: IComment) => {
    const questionId = comment.questionId;
    if (questionId) {
      const element = CommentsService.getQuestionFromPath(props.survey, questionId);
      if (element) {
        if (element.getType() === 'panel' || element.getType() === 'paneldynamic') {
          element.focusFirstQuestion();
        } else {
          element.focus(undefined, true);
        }
      }
    }
  }

  const [newComment, setNewComment] = React.useState<IComment>();

  const addNewComment = (questionId?: string, threadId?: string) => {
    const account = AuthService.account;

    const question = questionId && CommentsService.getQuestionFromPath(props.survey, questionId);
    const page = question && props.survey.getPageByElement(question);
    const questionTitle = questionId && CommentsService.getQuestionTitleForComment(props.survey, questionId);

    const newComment: IComment = {
      id: makeGuidString(),
      oid: account?.idTokenClaims?.oid,
      email: account?.username,
      username: account?.name,
      role: account?.idTokenClaims?.roles?.join(", ") ?? "course provider",
      message: '',
      timestamp: '',
      threadId: threadId,
      question: questionTitle,
      questionId: questionId,
      courseId: props.courseId,
      moduleId: props.moduleId,
      page: page ? page.name : props.pageId,
    }

    setNewComment(newComment);
    setEditCommentId(newComment.id);
  }

  const onReplyComment = (comment: IComment) => {
    addNewComment(comment.questionId, comment.threadId || comment.id);
  }

  const [allComments, setAllComments] = React.useState<IComment[]>([]);
  React.useEffect(() => {
    const comments = [...Object.values(allCommentSet), ...(newComment ? [newComment] : [])];
    setAllComments(comments);
    if (!comments.length) {
      setFilterQuestionId(undefined);
    }
  }, [allCommentSet, newComment]);

  const filteredComments = React.useMemo(() => {
    // Find parent IDs to identify orphaned comments
    const parentIds = new Set(allComments.map(comment => comment.id));
    
    return allComments
      .filter(comment => 
        // Include top-level comments (no threadId)
        (!comment.threadId || 
         // Include orphaned comments (threadId points to non-existent parent)
         (comment.threadId && !parentIds.has(comment.threadId)))
        && (!filterQuestionId || comment.questionId === filterQuestionId)
        && (!comment.moduleId || comment.moduleId === props.moduleId)
      )
      .sort(CommentsService.compareComment);
  }, [allComments, filterQuestionId]);

  const questions = React.useMemo(() => {
    const result = CommentsService.getQuestions(allCommentSet, props.moduleId);
    if (props.questionId && !result.some(x => x.questionId === props.questionId)) {
      const question = CommentsService.getQuestionFromPath(props.survey, props.questionId);
      result.unshift({
        moduleId: props.moduleId,
        questionId: props.questionId,
        title: question?.processedTitle || '',
        count: 0
      });
    }
    return result;
  }, [allCommentSet, props.questionId]);

  const onAddNewComment = (questionId?: string, threadId?: string) => {
    setFilterQuestionId(questionId);
    addNewComment(questionId, threadId);
  }

  React.useEffect(() => {
    if (props.questionId) {
      const questionCount = CommentsService.getQuestionCommentCount(allCommentSet, comment => comment.questionId === props.questionId && comment.moduleId === props.moduleId);
      if (questionCount === 0) {
        addNewComment(props.questionId);
      }
    }
    if (!filterQuestionId || filterQuestionId !== props.questionId) {
      setFilterQuestionId(props.questionId);
    }
  }, [props.questionId, props.triggerCount])

  return <Drawer 
    variant={props.allowPin ? 'alwaysOpen' : ''} 
    placement='right' 
    onClose={props.onClose} 
    isOpen={props.isOpen} 
    size={props.size || 'sm'}
    blockScrollOnMount={false} 
    trapFocus={false}
    >
    <DrawerContent marginTop={120} p='0'>
      <DrawerHeader px='4!important' borderBottomWidth='1px'>
        <HStack justify='space-between'>
          <Heading size='sm'>Comments</Heading>
          <CloseButton onClick={props.onClose} />
        </HStack>
        <HStack py='4'>
          <Select placeholder='All Questions' value={filterQuestionId} onChange={e => setFilterQuestionId(e.target.value)}>
            {questions.map(question => (
              <option key={question.questionId} value={question.questionId}>{`${truncate(question.title, 50)} (${question.count})`}</option>
            ))}
          </Select>
          <Button size='md' onClick={() => onAddNewComment(filterQuestionId)}>New...</Button>
        </HStack>
      </DrawerHeader>
      <DrawerBody px='4!important' py='0'>
        <List w='full' overflowY='auto'>
          {filteredComments.map(comment => (
            <CommentTread
              key={comment.id}
              comment={comment}
              allComments={allComments}
              survey={props.survey}
              editCommentId={editCommentId}
              onSend={onSendComment}
              onCancel={onCancelEdit}
              onDelete={onDeleteComment}
              onEdit={onEditComment}
              onReply={onReplyComment}
              onNavigate={onNavigate}
            />))}
        </List>
      </DrawerBody>
    </DrawerContent>
  </Drawer >
}